import { useEffect, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { useField } from 'formik';
import { useRecoilState, useRecoilValue } from 'recoil';
import { AdditionalTariffChargePayload, TransactionPricingCard } from 'types/graphql';

import { complianceChargeDefaultsAtom, transactionOfferAtom } from 'src/atoms';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { GET_TRANSACTION_CHARGES } from 'src/graphql/queries/getTransactionCharges.query';
import {
    SAFE_PAYMENTS_OPTIONS,
    WORLDPAY_OPTIONS,
} from 'src/pages/AddTransactionPricingPage/sections/CompliancePricing';

import { TRANSACTION_PRICING_OFFER_ID_KEY } from '../useTransactionPricing';

export type AdditionalCharge = {
    name?: string;
    label: string;
    creditCardPercentage?: number;
    creditCardPerItem?: number;
    debitCardPercentage?: number;
    debitCardPerItem?: number;
};

export const useTransactionData = () => {
    const { loading, error, data } = useQuery(GET_TRANSACTION_CHARGES);

    if (loading || error) return { loading, error, data: null };

    const { terminalCharges, onlineCharges } = data?.transactionCharges || {};

    const ADDITIONAL_CHARGES = terminalCharges?.additionalCharges || [];
    const ONLINE_ADDITIONAL_CHARGES = onlineCharges?.additionalCharges || [];

    const LOCALITY_CONSTANT = terminalCharges?.locality
        ? {
              intraRegional: {
                  ...terminalCharges.locality.intraRegional,
                  creditCardPerItem: 0.0,
                  debitCardPerItem: 0.0,
              },
              interRegional: terminalCharges.locality.interRegional,
          }
        : {};

    const ONLINE_LOCALITY_CONSTANT = onlineCharges?.locality
        ? {
              intraRegional: {
                  ...onlineCharges.locality.intraRegional,
                  creditCardPerItem: 0.0,
                  debitCardPerItem: 0.0,
              },
              interRegional: onlineCharges.locality.interRegional,
          }
        : {};

    return {
        loading,
        error,
        data,
        ADDITIONAL_CHARGES,
        ONLINE_ADDITIONAL_CHARGES,
        LOCALITY_CONSTANT,
        ONLINE_LOCALITY_CONSTANT,
    };
};

export const useAdditionalCharges = (serviceType) => {
    const { ADDITIONAL_CHARGES, ONLINE_ADDITIONAL_CHARGES } = useTransactionData();

    const additionalCharges = useMemo(() => {
        const newObject = {};
        let additionalChrgesType = [];

        if (serviceType && serviceType === 'ONLINE') {
            additionalChrgesType = ONLINE_ADDITIONAL_CHARGES || [];
        } else {
            additionalChrgesType = ADDITIONAL_CHARGES || [];
        }

        additionalChrgesType.forEach(
            ({
                name,
                creditCardPerItem,
                creditCardPercentage,
                debitCardPerItem,
                debitCardPercentage,
            }) => {
                newObject[name] = {
                    creditCardPerItem,
                    creditCardPercentage,
                    debitCardPerItem,
                    debitCardPercentage,
                };
            }
        );

        return newObject;
    }, [ADDITIONAL_CHARGES, ONLINE_ADDITIONAL_CHARGES]);

    return { additionalCharges };
};

export const useAdditionalTarrifs = () => {
    const selectedTransactionOffer = useRecoilValue(transactionOfferAtom);
    const [, , { setValue }] = useField('transactionCharges.additionalTariffs');
    const additionalTarrifCharges = useMemo(() => {
        return selectedTransactionOffer?.additionalTariffCharges ?? [];
    }, [selectedTransactionOffer]) as AdditionalTariffChargePayload[];

    useEffect(() => {
        const initialValues: Record<string, number> = {};

        additionalTarrifCharges.forEach(({ baseRate, fixedRate, type, name }) => {
            if (type === 'FIXED') {
                initialValues[`${name}`] = fixedRate;
            } else {
                initialValues[`${name}`] = baseRate;
            }
        });

        setValue(initialValues);
    }, [additionalTarrifCharges, setValue]);

    const editableFields = useMemo(() => {
        const editableFields = [];

        additionalTarrifCharges.forEach(({ type, name, lowestRate, highestRate }) => {
            if (type === 'PER_ITEM') {
                const editableField = {
                    name,
                    min: lowestRate,
                    max: highestRate,
                };
                editableFields.push(editableField);
            }
        });

        return editableFields;
    }, [additionalTarrifCharges]);

    return { editableFields };
};

export const useTransactionCharges = () => {
    const selectedTransactionOffer = useRecoilValue(transactionOfferAtom);
    const [{ value: transactionPricingOfferId }] = useField(
        `service.${TRANSACTION_PRICING_OFFER_ID_KEY}`
    );
    const { merchantApplication } = useAddMerchantContext();
    const [{ value: currentPersonalCards }, _, { setValue: setPersonalCards }] = useField(
        'transactionCharges.personalCards'
    );
    const [{ value: currentOtherCards }, __, { setValue: setOtherCards }] = useField(
        'transactionCharges.otherCards'
    );
    const [{ value: currentCommercialCards }, ___, { setValue: setCommercialCards }] = useField(
        'transactionCharges.commercialCards'
    );

    const transactionPricingCard = useMemo(() => {
        return selectedTransactionOffer?.transactionPricingCards ?? [];
    }, [selectedTransactionOffer]) as TransactionPricingCard[];

    const draftApplicationTransactionCharges =
        merchantApplication?.serviceRequirements?.transactionCharges;
    const draftApplicationSelectedTransactionPricingOffer =
        merchantApplication?.serviceRequirements?.service?.transactionPricingOfferId;

    const offerSelected =
        (selectedTransactionOffer?.id && transactionPricingOfferId !== '') ||
        draftApplicationSelectedTransactionPricingOffer !== '';

    const isNewSelectedOffer =
        (selectedTransactionOffer?.id !== undefined &&
            transactionPricingOfferId !== '' &&
            transactionPricingOfferId !== selectedTransactionOffer?.id?.toString()) ||
        (draftApplicationSelectedTransactionPricingOffer &&
            draftApplicationSelectedTransactionPricingOffer !==
                selectedTransactionOffer?.id?.toString());

    const shouldDisregardDraftApplicationValues =
        (transactionPricingOfferId !== '' &&
            transactionPricingOfferId !== selectedTransactionOffer?.id?.toString()) ||
        (draftApplicationSelectedTransactionPricingOffer !== '' &&
            transactionPricingOfferId !== draftApplicationSelectedTransactionPricingOffer);

    const shouldUpdateValues = offerSelected || isNewSelectedOffer;

    const { personalCards, commercialCards, otherCards } = useMemo(() => {
        const cardsArray = transactionPricingCard.map(
            ({
                percentageBaseRate,
                perItemBaseRate,
                baseRatePercentage,
                type,
                name,
                cardCategory,
            }) => {
                const isBoth = type === 'BOTH';
                const isPercentage = type === 'PERCENTAGE';

                const percentageBase = isBoth
                    ? baseRatePercentage
                    : isPercentage
                    ? percentageBaseRate
                    : 0;

                return {
                    percentageBaseRate: percentageBase,
                    perItemBaseRate,
                    refundPercentage: undefined,
                    refundCostPerItem: 0.14,
                    name,
                    cardCategory,
                };
            }
        );

        const personalCards = {};
        const commercialCards = {};
        const otherCards = {};

        cardsArray.forEach(({ cardCategory, name, ...rest }) => {
            if (cardCategory === 'PERSONAL_CARDS') {
                const draftApplicationPersonalCardValue =
                    draftApplicationTransactionCharges?.personalCards[`${name}`];

                /**
                 * Use selected offer values or if edited use draft values
                 * unless the edited selected offer is changed to another then show
                 * that one
                 */
                personalCards[`${name}`] = {
                    ...rest,
                    ...(shouldDisregardDraftApplicationValues
                        ? {}
                        : draftApplicationPersonalCardValue),
                };
            } else if (cardCategory === 'COMMERCIAL_CARDS') {
                const draftApplicationCommericalCardValue =
                    draftApplicationTransactionCharges?.commercialCards[`${name}`];
                /**
                 * Use selected offer values or if edited use draft values
                 * unless the edited selected offer is changed to another then show
                 * that one
                 */
                commercialCards[`${name}`] = {
                    ...rest,
                    ...(shouldDisregardDraftApplicationValues
                        ? {}
                        : draftApplicationCommericalCardValue),
                };
            } else if (cardCategory === 'OTHER') {
                const draftApplicationOtherCardValue =
                    draftApplicationTransactionCharges?.otherCards[`${name}`];
                /**
                 * Use selected offer values or if edited use draft values
                 * unless the edited selected offer is changed to another then show
                 * that one
                 */
                otherCards[`${name}`] = {
                    ...rest,
                    ...(shouldDisregardDraftApplicationValues
                        ? {}
                        : draftApplicationOtherCardValue),
                };
            }
        });

        return { personalCards, commercialCards, otherCards };
    }, [
        draftApplicationTransactionCharges?.commercialCards,
        draftApplicationTransactionCharges?.otherCards,
        draftApplicationTransactionCharges?.personalCards,
        transactionPricingCard,
        shouldDisregardDraftApplicationValues,
    ]);

    useEffect(() => {
        if (shouldUpdateValues) {
            setPersonalCards(personalCards);
        }
    }, [personalCards, setPersonalCards, shouldUpdateValues]);

    useEffect(() => {
        if (shouldUpdateValues) {
            setOtherCards(otherCards);
        }
    }, [shouldUpdateValues, otherCards, setOtherCards]);

    useEffect(() => {
        if (shouldUpdateValues) {
            setCommercialCards(commercialCards);
        }
    }, [commercialCards, shouldUpdateValues, setCommercialCards]);
};

export const useComplianceCharges = () => {
    const selectedTransactionOffer = useRecoilValue(transactionOfferAtom);
    const [_, __, { setValue: setComplianceCharges }] = useField(
        'transactionCharges.pciComplianceCharges'
    );
    const [___, setChargesDefault] = useRecoilState(complianceChargeDefaultsAtom);

    const COMBINED_DEFAULTS = [...WORLDPAY_OPTIONS, ...SAFE_PAYMENTS_OPTIONS];

    const { merchantApplication } = useAddMerchantContext();

    useEffect(() => {
        if (selectedTransactionOffer) {
            const draftApplicationComplianceCharges =
                merchantApplication?.serviceRequirements?.transactionCharges?.pciComplianceCharges;
            const charges = (selectedTransactionOffer?.compliancePricingCharges ?? []).map(
                ({ baseRate, fixedRate, name, type, highestRate, lowestRate, chargeType }) => {
                    // check value edited in application
                    const draftApplicationValue =
                        name === 'saferPaymentsProgrammeFee'
                            ? draftApplicationComplianceCharges?.worldPayCharges
                            : draftApplicationComplianceCharges?.additionChargers[name];

                    return {
                        amount:
                            type === 'FIXED'
                                ? fixedRate
                                : draftApplicationValue !== baseRate &&
                                  Number.isFinite(draftApplicationValue)
                                ? draftApplicationValue
                                : baseRate,
                        name,
                        type,
                        highestRate,
                        lowestRate,
                        hint: COMBINED_DEFAULTS.find((el) => {
                            return el.name === name;
                        }).tooltip,
                        label: COMBINED_DEFAULTS.find((el) => {
                            return el.name === name;
                        }).heading,
                        note: COMBINED_DEFAULTS.find((el) => {
                            return el.name === name;
                        }).note,
                        chargeType,
                    };
                }
            );

            setChargesDefault(charges);

            const worldPayCharges = charges.filter(({ chargeType }) => {
                return chargeType === 'WORLDPAY_PROGRAMME';
            });
            const additionChargers = charges.filter(({ chargeType }) => {
                return chargeType === 'SAFE_PAYMENTS';
            });

            const worldPayChargesData = {};
            const additionChargersData = {};

            worldPayCharges.forEach(({ name, amount }) => {
                worldPayChargesData[name] = amount;
            });

            additionChargers.forEach(({ name, amount }) => {
                additionChargersData[name] = amount;
            });

            setComplianceCharges({
                worldPayCharges: worldPayChargesData,
                additionChargers: additionChargersData,
            });
        }
    }, [selectedTransactionOffer?.id]);
};
